/**
 * 渠道模型库条目的完整编辑表单：类型、币种、刊例价、折扣、上下文、分时与图片 Token、视频价目。
 *
 * 背景：从旧的 ChannelModels 页拆出；Alice 的「报价」弹窗与登记弹窗在页面里，完整价目仍在这里改。
 * 设计意图：报价字段全部可空，空与 0 含义不同：空表示没维护（成本未知），0 表示确定免费。
 * 关键约束：保存会生成新配置版本；价格以字符串提交，后端按 Decimal 校验。
 */
import { useState } from 'react'
import { catalogApi } from '@ap/api-client/admin-catalog'
import type { ChannelModel, ChannelModelInput, ModelKind } from '@ap/api-client/types'
import { A } from '@ap/i18n/admin'
import { C, errorText } from '@ap/i18n/common'
import { useI18n } from '@ap/i18n'
import {
  Button,
  Card,
  Field,
  Form,
  Input,
  Modal,
  Notice,
  Select,
  Switch,
  Textarea,
  useToast,
} from '@ap/ui'
import { KINDS, isEmptyObj, num, orNull } from './shared.ts'

const showJson = (v: unknown) => (isEmptyObj(v) ? '' : JSON.stringify(v, null, 2))

export function ChannelModelForm({
  item,
  channelIds,
  onClose,
  onSaved,
}: {
  item: ChannelModel | 'new' | null
  channelIds: string[]
  onClose: () => void
  onSaved: () => void
}) {
  const { t, locale } = useI18n()
  const toast = useToast()
  const isNew = item === 'new'
  const [channel, setChannel] = useState('')
  const [upstream, setUpstream] = useState('')
  const [kind, setKind] = useState<ModelKind>('chat')
  const [currency, setCurrency] = useState<'USD' | 'CNY'>('USD')
  const [imageMode, setImageMode] = useState<'per_image' | 'token'>('per_image')
  const [enabled, setEnabled] = useState(true)
  const [f, setF] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [shown, setShown] = useState<typeof item>(null)

  if (item !== shown) {
    setShown(item)
    setError('')
    if (item) {
      const m = item === 'new' ? null : item
      setChannel(m?.channel_id ?? channelIds[0] ?? '')
      setUpstream(m?.upstream_model ?? '')
      setKind(m?.kind ?? 'chat')
      setCurrency(m?.price_currency ?? 'USD')
      setImageMode(m?.image_billing_mode ?? 'per_image')
      setEnabled(m?.enabled ?? true)
      setF({
        note: m?.note ?? '',
        list_prompt: num(m?.list_prompt ?? null),
        list_completion: num(m?.list_completion ?? null),
        list_cache_read: num(m?.list_cache_read ?? null),
        list_cache_write: num(m?.list_cache_write ?? null),
        list_per_image: num(m?.list_per_image ?? null),
        cost_discount: num(m?.cost_discount ?? null),
        context_length: m?.context_length == null ? '' : String(m.context_length),
        image_token_prices: showJson(m?.image_token_prices),
        video_pricing: showJson(m?.video_pricing),
        peak_pricing: showJson(m?.peak_pricing),
      })
    }
  }
  const set = (k: string) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }))

  const json = <T,>(name: string, text: string, fallback: T): T => {
    if (!text.trim()) return fallback
    try {
      return JSON.parse(text) as T
    } catch {
      throw new Error(t(A.models.badJson, { name }))
    }
  }

  const submit = async () => {
    setBusy(true)
    setError('')
    try {
      const body: ChannelModelInput = {
        kind,
        note: f.note.trim(),
        price_currency: currency,
        list_prompt: orNull(f.list_prompt),
        list_completion: orNull(f.list_completion),
        list_cache_read: orNull(f.list_cache_read),
        list_cache_write: orNull(f.list_cache_write),
        list_per_image: orNull(f.list_per_image),
        image_billing_mode: imageMode,
        image_token_prices: json(t(A.models.imageTokenPrices), f.image_token_prices, {}),
        video_pricing: json(t(A.models.videoPricing), f.video_pricing, {}),
        peak_pricing: json(t(A.models.peak), f.peak_pricing, null),
        cost_discount: orNull(f.cost_discount),
        context_length: f.context_length.trim() ? Number(f.context_length) : null,
        enabled,
      }
      const r = await catalogApi.putChannelModel(channel, upstream.trim(), body)
      toast.ok(t(A.models.savedVersion, { v: r.config_version }))
      onSaved()
    } catch (e) {
      setError(e instanceof Error && !('code' in e) ? e.message : errorText(e, locale))
    } finally {
      setBusy(false)
    }
  }

  const price = (k: string, label: string) => (
    <Field label={label}>
      <Input inputMode="decimal" pattern="[0-9]+(\.[0-9]+)?" value={f[k] ?? ''} onChange={set(k)} />
    </Field>
  )
  const textual = kind !== 'image' && kind !== 'video'

  return (
    <Modal open={item !== null} onClose={onClose} busy={busy} wide title={isNew ? t(A.cm.add) : `${channel} / ${upstream}`}>
      <Form onSubmit={submit}>
        {error && <Notice tone="danger">{error}</Notice>}
        <div className="ap-grid2">
          <Field label={t(A.cm.channel)}>
            <Select required disabled={!isNew} value={channel} onChange={(e) => setChannel(e.target.value)} options={channelIds.map((c) => ({ value: c, label: c }))} />
          </Field>
          <Field label={t(A.cm.upstreamModel)}>
            <Input mono required disabled={!isNew} maxLength={200} value={upstream} onChange={(e) => setUpstream(e.target.value)} />
          </Field>
          <Field label={t(A.models.kind)}>
            <Select value={kind} onChange={(e) => setKind(e.target.value as ModelKind)} options={KINDS.map((k) => ({ value: k, label: k }))} />
          </Field>
          <Field label={t(A.models.currency)}>
            <Select value={currency} onChange={(e) => setCurrency(e.target.value as 'USD' | 'CNY')} options={[{ value: 'USD', label: 'USD' }, { value: 'CNY', label: 'CNY' }]} />
          </Field>
        </div>
        {textual && (
          <div className="ap-grid2">
            {price('list_prompt', t(A.cm.listPrompt))}
            {price('list_completion', t(A.cm.listCompletion))}
            {price('list_cache_read', t(A.cm.listCacheRead))}
            {price('list_cache_write', t(A.cm.listCacheWrite))}
          </div>
        )}
        {kind === 'image' && (
          <div className="ap-grid2">
            <Field label={t(A.models.imageMode)}>
              <Select
                value={imageMode}
                onChange={(e) => setImageMode(e.target.value as 'per_image' | 'token')}
                options={[
                  { value: 'per_image', label: t(A.models.imageModePerImage) },
                  { value: 'token', label: t(A.models.imageModeToken) },
                ]}
              />
            </Field>
            {imageMode === 'per_image' ? price('list_per_image', t(A.cm.listPerImage)) : null}
          </div>
        )}
        <div className="ap-grid2">
          <Field label={t(A.cm.discount)} hint={t(A.cm.discountHint)}>
            <Input inputMode="decimal" pattern="[0-9]+(\.[0-9]{1,6})?" value={f.cost_discount ?? ''} onChange={set('cost_discount')} />
          </Field>
          <Field label={t(A.cm.context)}>
            <Input type="number" min={1} value={f.context_length ?? ''} onChange={set('context_length')} />
          </Field>
        </div>
        <Card title={t(A.models.advanced)}>
          <div className="ap-stack" style={{ gap: 12 }}>
            {textual && (
              <Field label={t(A.models.peak)}>
                <Textarea rows={4} spellCheck={false} className="ap-mono" value={f.peak_pricing ?? ''} onChange={set('peak_pricing')} />
              </Field>
            )}
            {kind === 'image' && (
              <Field label={t(A.models.imageTokenPrices)}>
                <Textarea rows={4} spellCheck={false} className="ap-mono" value={f.image_token_prices ?? ''} onChange={set('image_token_prices')} />
              </Field>
            )}
            {kind === 'video' && (
              <Field label={t(A.models.videoPricing)}>
                <Textarea rows={6} spellCheck={false} className="ap-mono" value={f.video_pricing ?? ''} onChange={set('video_pricing')} />
              </Field>
            )}
          </div>
        </Card>
        <Field label={t(A.cm.note)}>
          <Input maxLength={500} value={f.note ?? ''} onChange={set('note')} />
        </Field>
        <Switch checked={enabled} onChange={setEnabled} label={t(A.models.enabled)} />
        <div className="ap-row" style={{ justifyContent: 'flex-end' }}>
          <Button onClick={onClose} disabled={busy}>
            {t(C.cancel)}
          </Button>
          <Button type="submit" variant="primary" loading={busy}>
            {t(C.save)}
          </Button>
        </div>
      </Form>
    </Modal>
  )
}

